/**
 * PromptMySite Widget - Script Tag Initialization
 *
 * Handles auto-initialization from script tag data attributes.
 * Detects the embed script tag and extracts configuration.
 */

import { parseDataAttributes, mergeConfig } from '../core/config';
import { debug } from '../core/logger';
import { PromptMySiteWidget } from './widget';
import { setActiveWidget, updateGlobalApiWithWidget } from './global-api';

// =============================================================================
// SCRIPT TAG DETECTION
// =============================================================================

/**
 * Find the script tag that loaded this embed script
 */
function findEmbedScript(): HTMLScriptElement | null {
  const scripts = document.getElementsByTagName('script');

  // Priority 1: script with data-workspace attribute (most explicit)
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (script?.hasAttribute('data-workspace')) {
      return script;
    }
  }

  // Priority 2: script with embed.js in src (our CDN bundle)
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (script?.src.includes('embed.js')) {
      return script;
    }
  }

  // Priority 3: script with promptmysite in src AND a workspace query param
  // (avoids matching platform-hosted loader scripts that lack workspace context)
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (script?.src.includes('promptmysite') && script.src.includes('workspace=')) {
      return script;
    }
  }

  // Fallback to document.currentScript
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }

  return null;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize widget from script tag data attributes
 */
export function initFromScriptTag(): void {
  const currentScript = findEmbedScript();

  if (!currentScript) {
    // No script found - just expose the API for programmatic use
    debug(' No script tag found, API available for programmatic init');
    return;
  }

  // Parse configuration from data attributes
  const partialConfig = parseDataAttributes(currentScript);

  // If no workspace ID, just expose API for programmatic init
  if (!partialConfig.workspaceId) {
    debug(' No data-workspace set, API available for programmatic init');
    return;
  }

  try {
    const config = mergeConfig(partialConfig);
    const widget = new PromptMySiteWidget(config);

    // Store as active widget
    setActiveWidget(widget);

    // Update global API to point to new widget
    updateGlobalApiWithWidget(widget);

    // Initialize asynchronously (fetches workspace config)
    widget.init().then(() => {
      debug(' Widget ready');
    }).catch((error) => {
      console.error('[PromptMySite] Widget initialization error:', error);
    });
  } catch (error) {
    console.error('[PromptMySite] Failed to initialize widget:', error);
  }
}

// =============================================================================
// AUTO-INITIALIZATION
// =============================================================================

/**
 * Set up auto-initialization when DOM is ready
 */
export function setupAutoInit(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFromScriptTag);
  } else {
    // DOM already loaded, initialize immediately
    initFromScriptTag();
  }
}
